/**
 * capabilityService — Funzioni pure per gating fail-safe (MOD-4-A).
 *
 * Risponde alla domanda: "il tenant X ha la capability Y attiva?"
 *
 * Pure functions, ZERO side effects, ZERO dipendenza Firestore SDK.
 * Riusabili sia da frontend (hook React) sia da backend (CF middleware).
 *
 * Casi gestiti:
 *  - capability in `tenant.modules.base[]` (deriva da tier) → attivo
 *  - capability in `tenant.modules.addon[]` (acquistato a la carte) → attivo
 *  - capability in `tenant.modules.override[]` (regalia super_admin) → attivo
 *  - capability in `tenant.modules.expired[]` (non pagato/scaduto) → NON attivo
 *    anche se presente in base/addon/override
 *  - super_admin (claim is_super_admin=true) → BYPASS tutti i check
 *  - impersonation (claim is_impersonation=true) → BYPASS tutti i check
 *    (il super_admin che impersona vede tutto del tenant impersonato)
 *  - capability del piano che non riguarda i prodotti dell'azienda (`modules.non_pertinenti`, 09/10/2026)
 *    → NON attiva dal piano, source 'non_pertinente'; un'aggiunta o un'eccezione esplicita vince.
 *    ⚠️ Le regole Firestore NON applicano questo filtro, per scelta: la pertinenza decide cosa si VEDE, non
 *    cosa l'azienda ha il diritto di usare (l'ha pagata col piano). Aggiungerlo alle regole costerebbe peso
 *    senza proteggere niente. Regola completa in `pertinenza.ts`.
 *
 * Vedi: docs/superpowers/specs/2026-05-17-modules-audit.md
 *       CLAUDE.md sezione "Catalogo moduli persistito (MOD-2)"
 *
 * @since 2026-05-17 (MOD-4-A)
 */

// Import esplicito file (no barrel `../types`) per compatibilita' con
// node16 moduleResolution usato da functions/ (richiede .js extension).
// Il frontend usa moduleResolution bundler e accetta entrambe le forme
// e tollera l'estensione .js sui sorgenti .ts.
import type { TenantModules } from '../types/module.types.js';

// ============================================================================
// CALLER CONTEXT
// ============================================================================

/**
 * Contesto del chiamante per il check di capability.
 *
 * Il super_admin (claim JWT `is_super_admin === true`) bypassa il check.
 * Stesso per l'impersonation (claim `is_impersonation === true`, che e'
 * sempre originata da super_admin).
 */
export interface CapabilityCheckerContext {
  is_super_admin?: boolean;
  is_impersonation?: boolean;
  /**
   * Posti misti (seat_tier per utente, 2026-07-18).
   *
   * Capability del POSTO assegnato all'utente (`app_users.seat_capabilities`,
   * denormalizzate e scritte solo server-side da setUserSeatTier).
   *
   * Semantica: il seat limita SOLO le capability derivate dal tier
   * (`modules.base`); addon/override del tenant restano tenant-wide.
   * `undefined`/`null` = nessun posto assegnato → nessuna restrizione
   * (comportamento bit-identico a prima dei posti misti).
   *
   * Vedi: docs/01-ARCHITECTURE/TIER-CAPABILITY-VISIBILITY-MODEL.md §4-ter
   */
  seat_capabilities?: readonly string[] | null;

  /**
   * Il posto decide TUTTO (02/11/2026, posto fuori dalla scala: Presenze).
   *
   * `app_users.seat_esclusivo`, scritto solo dal server insieme a `seat_capabilities` quando il posto non è un piano
   * della scala. Con `true` una funzione vale solo se il posto la comprende, da qualunque parte l'azienda la abbia
   * (piano, aggiunte, regali, app regalate a tutti): chi timbra soltanto non riceve YumaCALL perché l'azienda l'ha in
   * regalo. Rifiuto con `source: 'seat'`. `undefined`/`null`/`false` = regola di sempre (il posto filtra solo il piano).
   */
  seat_esclusivo?: boolean | null;

  /**
   * App add-on PER-POSTO (yumacall/yumawork/…, 2026-07-20).
   *
   * Le app Android vendute come add-on per-posto sono attivate sul singolo
   * utente via il campo `app_users.seat_apps: string[]` (es. `['yumacall']`),
   * scritto solo server-side. A differenza di `seat_capabilities` (che FILTRA
   * le capability del tier), `seat_apps` CONCEDE le capability-app al posto.
   *
   * Semantica (vedi ramo `app_addon_capabilities` in checkCapability):
   *  - una capability marcata come app-add-on (presente in `app_addon_capabilities`)
   *    NON deriva più dal tier: è concessa al posto SOLO se il suo id è in
   *    `seat_apps`, OPPURE se il tenant la concede a tutti via `modules.override`
   *    (caso dei tenant interni billing-exempt, che le tengono per ogni posto).
   *  - `undefined`/`null` = nessuna app assegnata al posto (default sicuro:
   *    le capability-app restano negate a meno di override tenant-wide).
   *
   * Vedi: docs/01-ARCHITECTURE/TIER-CAPABILITY-VISIBILITY-MODEL.md §4-quater
   */
  seat_apps?: readonly string[] | null;

  /**
   * Insieme (data-driven) degli id capability che sono "app add-on per-posto",
   * derivato dai DATI della collezione `app_addons` (campo `capability` di ogni
   * doc), MAI hardcodato: il motore non "sa" cosa sia YumaCALL/YumaWORK.
   *
   * Serve a decidere QUALI capability vanno trattate col ramo per-posto
   * `seat_app` (concesse via `seat_apps`/`override`) invece del ramo tier
   * (`base`). Passato dal chiamante che ha caricato `app_addons`
   * (requireCapability lato backend, provider capability lato frontend).
   *
   * `undefined`/`null`/insieme vuoto = nessuna capability è un'app add-on →
   * il ramo per-posto è INERTE e ogni capability segue il percorso classico
   * (retrocompat totale: i chiamanti che non conoscono le app non cambiano
   * comportamento).
   */
  app_addon_capabilities?: ReadonlySet<string> | null;
}

// ============================================================================
// INPUT MINIMO PER IL CHECK
// ============================================================================

/**
 * Dati minimi necessari dal tenant doc per fare il check.
 * Accetta sia `Tenant` (frontend) sia il raw doc data (backend admin SDK).
 */
export interface TenantCapabilitySnapshot {
  /** Tier-derived modules (base). Se assente, fallback a tutti i moduli core. */
  modules?: TenantModules;
  /**
   * I posti-app comprati e assegnati (app a pagamento per posto, `seat.app_quota_by_id` / `seat.used_apps_by_id`):
   * li legge solo `aziendaHaCapability`, la domanda fatta per l'azienda intera.
   */
  seat?: {
    app_quota_by_id?: Readonly<Record<string, number>>;
    used_apps_by_id?: Readonly<Record<string, number>>;
  } | null;
}

// ============================================================================
// FUNZIONE PRINCIPALE
// ============================================================================

/**
 * Ritorna true se il tenant ha la capability attiva, false altrimenti.
 *
 * Logica:
 *  1. Se context.is_super_admin || context.is_impersonation → TRUE (bypass)
 *  2. Se capability in `expired` → FALSE (revoked)
 *  3. Se capability è un'app add-on per-posto (in `app_addon_capabilities`):
 *     TRUE se in `override` (tenant-wide) o in `seat_apps` (posto), altrimenti FALSE
 *  4. Se capability in `base` ∩ seat ∪ `addon` ∪ `override` → TRUE
 *  5. Altrimenti → FALSE
 */
export function hasCapability(
  tenant: TenantCapabilitySnapshot | null | undefined,
  capabilityId: string,
  context: CapabilityCheckerContext = {},
): boolean {
  // Delega a checkCapability: unica implementazione della logica
  // (bypass → expired → base∩seat → addon → override), zero drift.
  return checkCapability(tenant, capabilityId, context).allowed;
}

/**
 * Versione che ritorna un oggetto dettagliato con la sorgente.
 * Utile per debug + per scrivere audit event in caso di accesso negato.
 *
 * `source: 'seat'` (posti misti) = la capability e' attiva per il TENANT
 * (via tier/modules.base) ma NON e' inclusa nel posto assegnato all'utente:
 * la UI upsell deve proporre "chiedi al tuo amministratore un posto
 * superiore" invece dell'upgrade del piano aziendale.
 *
 * `source: 'seat_app'` (app add-on per-posto) = la capability e' un'app
 * add-on (yumacall/yumawork/…): allowed=true quando il posto la include in
 * `seat_apps`; allowed=false quando non la include (e il tenant non la
 * concede tenant-wide via override). In caso di negato la UI upsell deve
 * proporre "aggiungi l'app al tuo posto" (non l'upgrade del piano ne' del posto).
 */
/**
 * `source: 'non_pertinente'` (09/10/2026) = la capability arriva dal piano ma non riguarda nessuno dei prodotti
 * che l'azienda vende (il drone per chi fa infissi). Non è una cosa da comprare: la UI la NASCONDE, senza
 * proposte di passaggio di piano.
 */
export type CapabilitySource = 'super_admin' | 'impersonation' | 'base' | 'addon' | 'override' | 'expired' | 'seat' | 'seat_app' | 'non_pertinente' | 'absent';

export interface CapabilityCheckResult {
  allowed: boolean;
  source: CapabilitySource;
}

/**
 * Il motivo di un rifiuto, come il server lo scrive in `details.codice` (27/10/2026, TKT-2026-0094).
 *
 * Il messaggio di testo del server nominava l'id tecnico («il modulo "estrazione_bollette"») e non diceva a chi
 * chiedere: Pierfilippo Fornasiero, posto PRO, l'ha girato a Yuma che non sapeva cosa rispondere. Le due strade sono
 * diverse e chi legge deve sapere quale prendere:
 * - `posto_non_include`: l'azienda la ha, il posto della persona no → l'amministratore le dà un posto superiore;
 * - `app_non_sul_posto`: è un'app a pagamento per posto non assegnata → l'amministratore la aggiunge al posto;
 * - `modulo_non_attivo`: l'azienda non la ha (mai attivata, scaduta, non pertinente) → l'amministratore la attiva.
 * Gli id sono chiavi (mai tradotti); la frase la sceglie chi la mostra, nella lingua di chi legge.
 */
export type CodiceRifiutoCapability = 'posto_non_include' | 'app_non_sul_posto' | 'modulo_non_attivo';

/** I dettagli che accompagnano un rifiuto del server (`HttpsError.details`). */
export interface DettagliRifiutoCapability {
  codice: CodiceRifiutoCapability;
  capability: string;
}

/** Da «perché è negata» a «cosa deve fare chi legge»: UNA regola per server, gestionale e Yuma. */
export function codiceDelRifiuto(source: CapabilitySource): CodiceRifiutoCapability {
  if (source === 'seat') return 'posto_non_include';
  if (source === 'seat_app') return 'app_non_sul_posto';
  return 'modulo_non_attivo';
}

const CODICI_RIFIUTO: readonly CodiceRifiutoCapability[] = ['posto_non_include', 'app_non_sul_posto', 'modulo_non_attivo'];

/** Legge i dettagli di un rifiuto da un errore qualunque (una callable fallita); `null` se non è un rifiuto di capability. */
export function dettagliRifiutoCapability(errore: unknown): DettagliRifiutoCapability | null {
  const dettagli = (errore as { details?: { codice?: unknown; capability?: unknown } } | null | undefined)?.details;
  const codice = dettagli?.codice;
  const capability = dettagli?.capability;
  if (typeof codice !== 'string' || typeof capability !== 'string' || !capability) return null;
  if (!(CODICI_RIFIUTO as readonly string[]).includes(codice)) return null;
  return { codice: codice as CodiceRifiutoCapability, capability };
}

export function checkCapability(
  tenant: TenantCapabilitySnapshot | null | undefined,
  capabilityId: string,
  context: CapabilityCheckerContext = {},
): CapabilityCheckResult {
  const esito = esitoSenzaPostoEsclusivo(tenant, capabilityId, context);
  if (context.seat_esclusivo !== true || esito.source === 'super_admin' || esito.source === 'impersonation') return esito;
  if ((context.seat_capabilities ?? []).includes(capabilityId)) return esito;
  // Posto fuori dalla scala (02/11/2026): vale solo ciò che il posto comprende. Ciò che l'azienda ha (o venderebbe al
  // posto) diventa «chiedi un posto superiore»; ciò che l'azienda non ha resta com'era (scaduta, assente, non pertinente).
  if (esito.allowed || esito.source === 'seat' || esito.source === 'seat_app') return { allowed: false, source: 'seat' };
  return esito;
}

/** La regola di sempre: il posto della scala filtra solo il piano, aggiunte e regali valgono per tutti. */
function esitoSenzaPostoEsclusivo(
  tenant: TenantCapabilitySnapshot | null | undefined,
  capabilityId: string,
  context: CapabilityCheckerContext,
): CapabilityCheckResult {
  if (context.is_super_admin === true) return { allowed: true, source: 'super_admin' };
  if (context.is_impersonation === true) return { allowed: true, source: 'impersonation' };

  if (!tenant?.modules) return { allowed: false, source: 'absent' };
  const m = tenant.modules;
  const expired = m.expired as readonly string[] | undefined;
  const base = m.base as readonly string[] | undefined;
  const addon = m.addon as readonly string[] | undefined;
  const override = m.override as readonly string[] | undefined;

  if (expired?.includes(capabilityId)) return { allowed: false, source: 'expired' };

  // === APP ADD-ON PER-POSTO (2026-07-20) — ramo ADDITIVO, data-driven ===
  // Se la capability è marcata come app add-on (presente nell'insieme
  // `app_addon_capabilities`, derivato dai DATI di `app_addons`, MAI hardcodato)
  // NON segue il percorso del tier (`base`): le app sono add-on per-posto, non
  // capability di piano. È concessa quando:
  //  (a) il tenant la regala a TUTTI i posti via `modules.override` — è il
  //      meccanismo dei tenant interni billing-exempt (Vivisolar & co.), che
  //      mantengono le app per ogni utente → source 'override', tenant-wide;
  //  (b) altrimenti, SOLO se il posto dell'utente la include in `seat_apps`
  //      → source 'seat_app'.
  // Se `app_addon_capabilities` è assente/null (chiamante che non conosce le
  // app: retrocompat) questo ramo è INERTE e si prosegue col percorso classico.
  const appAddonCaps = context.app_addon_capabilities;
  if (appAddonCaps != null && appAddonCaps.has(capabilityId)) {
    // Override tenant-wide vince (tenant interni): l'app vale per ogni posto.
    if (override?.includes(capabilityId)) return { allowed: true, source: 'override' };
    const seatApps = context.seat_apps;
    if (seatApps != null && seatApps.includes(capabilityId)) {
      return { allowed: true, source: 'seat_app' };
    }
    // App non assegnata a questo posto e non concessa tenant-wide: upsell
    // dedicato "aggiungi l'app al tuo posto".
    return { allowed: false, source: 'seat_app' };
  }

  // Posti misti: il seat limita SOLO il ramo `base` (capability del tier).
  // seat_capabilities assente/null → nessuna restrizione (retrocompat totale).
  const seatCaps = context.seat_capabilities;
  const inBase = base?.includes(capabilityId) === true;
  // Pertinenza (09/10/2026): il piano dà solo ciò che riguarda i prodotti dell'azienda.
  const pertinente = !(m.non_pertinenti as readonly string[] | undefined)?.includes(capabilityId);
  if (inBase && pertinente && (seatCaps == null || seatCaps.includes(capabilityId))) {
    return { allowed: true, source: 'base' };
  }
  // addon/override sono tenant-wide (verticali di settore, regalie super_admin):
  // valgono per tutti i posti, il seat non li filtra.
  if (addon?.includes(capabilityId)) return { allowed: true, source: 'addon' };
  if (override?.includes(capabilityId)) return { allowed: true, source: 'override' };

  // Dal piano, ma non riguarda i prodotti dell'azienda: si nasconde, non si vende.
  if (inBase && !pertinente) return { allowed: false, source: 'non_pertinente' };
  // La capability era nel tier del tenant ma il posto dell'utente non la
  // include: sorgente dedicata per l'upsell "chiedi un posto superiore".
  if (inBase) return { allowed: false, source: 'seat' };

  return { allowed: false, source: 'absent' };
}

/**
 * Posti misti — filtra `modules.base` con le capability del posto.
 *
 * Helper per i punti che costruiscono la lista "capability attive" a mano
 * (Sidebar, ProtectedRoute, UIPermissionService): il seat interseca SOLO
 * la lista `base`; addon/override NON vanno filtrati.
 * seat_capabilities assente/null → ritorna base invariata (retrocompat).
 */
/**
 * «L'AZIENDA ce l'ha?» — la domanda di chi guarda l'azienda intera, non una persona (09/10/2026: la pagina «App per
 * il telefono» mostra all'amministratore le app della SUA AZIENDA, anche quelle che lui non ha sul suo posto).
 * - App a pagamento per posto (`app_addon_capabilities`, dai dati di `app_addons`): sì se l'azienda l'ha accesa per
 *   tutti (`modules.override`, le aziende interne) oppure se ne ha comprata o assegnata almeno una
 *   (`seat.app_quota_by_id` / `seat.used_apps_by_id`).
 * - Tutto il resto: la stessa risposta di `checkCapability`, senza il filtro del posto di una persona (piano
 *   pertinente ai prodotti venduti, aggiunte, eccezioni, scadenze).
 * Senza `app_addon_capabilities` (dati non ancora letti) le app seguono il percorso classico: aziende interne sì, le
 * altre no finché i dati non arrivano. Mai un sì in più.
 */
export function aziendaHaCapability(
  tenant: TenantCapabilitySnapshot | null | undefined,
  capabilityId: string,
  context: Pick<CapabilityCheckerContext, 'is_super_admin' | 'is_impersonation' | 'app_addon_capabilities'> = {},
): boolean {
  if (context.is_super_admin === true || context.is_impersonation === true) return true;
  if (!tenant?.modules) return false;
  const m = tenant.modules;
  if ((m.expired as readonly string[] | undefined)?.includes(capabilityId)) return false;
  if (context.app_addon_capabilities?.has(capabilityId)) {
    if ((m.override as readonly string[] | undefined)?.includes(capabilityId)) return true;
    const comprate = tenant.seat?.app_quota_by_id?.[capabilityId] ?? 0;
    const assegnate = tenant.seat?.used_apps_by_id?.[capabilityId] ?? 0;
    return comprate > 0 || assegnate > 0;
  }
  return checkCapability(tenant, capabilityId, { app_addon_capabilities: context.app_addon_capabilities }).allowed;
}

/**
 * Le capability che il PIANO dà a questa persona: `modules.base` meno quelle che non riguardano i prodotti
 * dell'azienda (`non_pertinenti`), intersecata col posto. È la lista che Sidebar e UIPermissionService uniscono
 * ad aggiunte ed eccezioni: la stessa regola di `checkCapability`, in un punto solo.
 */
export function capabilityDalPiano(
  modules: { base?: readonly string[]; non_pertinenti?: readonly string[] } | null | undefined,
  seatCapabilities?: readonly string[] | null,
): string[] {
  const fuori = new Set<string>(modules?.non_pertinenti ?? []);
  return filterBaseBySeat(modules?.base, seatCapabilities).filter((c) => !fuori.has(c));
}

/**
 * Le capability ATTIVE per questa persona: (piano ∩ posto) ∪ aggiunte ∪ eccezioni − scadute. È la regola con cui il menu
 * (Sidebar) e le rotte (`UIPermissionService.canViewModule`) decidono cosa si vede e cosa si apre, in un punto solo
 * (30/10/2026: le rotte avevano la loro copia, senza le eccezioni, e i Permessi edilizi regalati dalla console stavano
 * nel menu ma la pagina rimandava alla scrivania). Senza posto è lo stesso elenco di `getActiveCapabilities`.
 */
export function capabilityAttiveDellaPersona(
  modules:
    | { base?: readonly string[]; addon?: readonly string[]; override?: readonly string[]; expired?: readonly string[]; non_pertinenti?: readonly string[] }
    | null
    | undefined,
  seatCapabilities?: readonly string[] | null,
  seatEsclusivo?: boolean | null,
): string[] {
  if (!modules) return [];
  const scadute = new Set<string>(modules.expired ?? []);
  const attive = new Set<string>([...capabilityDalPiano(modules, seatCapabilities), ...(modules.addon ?? []), ...(modules.override ?? [])]);
  // Posto fuori dalla scala (02/11/2026): aggiunte e regali dell'azienda valgono solo se il posto li comprende.
  const delPosto = seatEsclusivo === true ? new Set<string>(seatCapabilities ?? []) : null;
  return [...attive].filter((c) => !scadute.has(c) && (delPosto === null || delPosto.has(c)));
}

export function filterBaseBySeat(
  base: readonly string[] | undefined,
  seatCapabilities?: readonly string[] | null,
): string[] {
  const baseArr = [...(base ?? [])];
  if (seatCapabilities == null) return baseArr;
  return baseArr.filter((c) => seatCapabilities.includes(c));
}

/**
 * Helper varianti: hasAny / hasAll.
 */
export function hasAnyCapability(
  tenant: TenantCapabilitySnapshot | null | undefined,
  capabilityIds: readonly string[],
  context: CapabilityCheckerContext = {},
): boolean {
  return capabilityIds.some((id) => hasCapability(tenant, id, context));
}

export function hasAllCapabilities(
  tenant: TenantCapabilitySnapshot | null | undefined,
  capabilityIds: readonly string[],
  context: CapabilityCheckerContext = {},
): boolean {
  return capabilityIds.every((id) => hasCapability(tenant, id, context));
}

/**
 * Ritorna la lista COMPLETA delle capability attive del tenant.
 *
 * Utile per la console super_admin (visualizzazione) e per snapshot/audit.
 * Esclude le capability in `expired`.
 */
export function getActiveCapabilities(
  tenant: TenantCapabilitySnapshot | null | undefined,
): string[] {
  if (!tenant?.modules) return [];
  const m = tenant.modules;
  const fuori = new Set<string>((m.non_pertinenti ?? []) as readonly string[]);
  const all = new Set<string>([
    ...((m.base ?? []) as readonly string[]).filter((c) => !fuori.has(c)),
    ...(m.addon ?? []),
    ...(m.override ?? []),
  ]);
  for (const e of m.expired ?? []) all.delete(e);
  return [...all].sort();
}
